import React, { useState, useEffect, useRef } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import "../styles/contact.css";
import { useTranslation } from "react-i18next";
import { CONTACT_INFO, EMAIL_INFO } from "../constants/info";
import contactUs from "../images/contact-us.png";
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface ContactUsPageProps {
  className?: string;
}

const ContactUsPage: React.FC<ContactUsPageProps> = ({ className = "" }) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 },
    );

    if (formRef.current) observer.observe(formRef.current);
    if (mapRef.current) observer.observe(mapRef.current);

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: "" });

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    }, 1500);
  };

  return (
    <div className={`min-h-screen bg-[#f2e6db] ${className}`}>
      <div className="pt-10 h-48 py-2 sm:h-96 overflow-hidden flex items-center justify-center mx-4">
        {/* Overlay */}
        <img
          src={contactUs}
          alt="Contact Us"
          className="w-full h-48 object-cover sm:w-auto sm:h-auto transition-transform bg-[#0d223f] duration-500 group-hover:scale-110"
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-1 sm:py-10 pt-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div
            ref={formRef}
            className="opacity-0 transform translate-y-8 transition-all duration-700"
          >
            <div className="glass-effect-card rounded-3xl p-8 md:p-12 transition-all duration-300">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Input */}
                <div className="relative group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-full focus:outline-none focus:border-[#0d223f] transition-all duration-300 peer"
                    placeholder=" "
                  />
                  <label
                    className={`absolute ${i18n.language === "ar" ? "right-6" : "left-6"} top-4 text-gray-500 transition-all duration-300 pointer-events-none peer-focus:text-sm peer-focus:-top-2 peer-focus: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-focus:bg-white peer-focus:px-2 peer-focus:text-[#0d223f] peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-[:not(:placeholder-shown)]:bg-white peer-[:not(:placeholder-shown)]:px-2`}
                  >
                    <span className="text-red-500">* </span>
                    {t("Name")}
                  </label>
                </div>

                {/* Email Input */}
                <div className="relative group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-full focus:outline-none focus:border-[#0d223f] transition-all duration-300 peer"
                    placeholder=" "
                  />
                  <label
                    className={`absolute ${i18n.language === "ar" ? "right-6" : "left-6"} top-4 text-gray-500 transition-all duration-300 pointer-events-none peer-focus:text-sm peer-focus:-top-2 peer-focus: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-focus:bg-white peer-focus:px-2 peer-focus:text-[#0d223f] peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-[:not(:placeholder-shown)]:bg-white peer-[:not(:placeholder-shown)]:px-2`}
                  >
                    <span className="text-red-500">* </span>
                    {t("E-mail")}
                  </label>
                </div>

                {/* Phone Input */}
                <div className="relative group">
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-full focus:outline-none focus:border-[#0d223f] transition-all duration-300 peer"
                    placeholder=" "
                  />
                  <label
                    className={`absolute ${i18n.language === "ar" ? "right-6" : "left-6"} top-4 text-gray-500 transition-all duration-300 pointer-events-none peer-focus:text-sm peer-focus:-top-2 peer-focus: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-focus:bg-white peer-focus:px-2 peer-focus:text-[#0d223f] peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-[:not(:placeholder-shown)]:bg-white peer-[:not(:placeholder-shown)]:px-2`}
                  >
                    <span className="text-red-500">* </span>
                    {t("Phone number")}
                  </label>
                </div>

                {/* Message Textarea */}
                <div className="relative group">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-3xl focus:outline-none focus:border-[#0d223f] transition-all duration-300 resize-none peer"
                    placeholder=" "
                  ></textarea>
                  <label
                    className={`absolute ${i18n.language === "ar" ? "right-6" : "left-6"} top-4 text-gray-500 transition-all duration-300 pointer-events-none peer-focus:text-sm peer-focus:-top-2 peer-focus: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-focus:bg-white peer-focus:px-2 peer-focus:text-[#0d223f] peer-[:not(:placeholder-shown)]:text-sm peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]: ${i18n.language === "ar" ? "right-4" : "left-4"} peer-[:not(:placeholder-shown)]:bg-white peer-[:not(:placeholder-shown)]:px-2`}
                  >
                    {t("Message")}
                  </label>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-[#0d223f] text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center gap-2 ${
                      isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("Sending...")}
                      </>
                    ) : isSuccess ? (
                      <>
                        <span>{t("✓ Sent!")}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t("Sending a message")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Contact Info & Map */}
          <div
            ref={mapRef}
            className="opacity-0 transform translate-y-8 transition-all duration-700 space-y-8"
          >
            {/* Contact Information Cards */}
            <div className="space-y-4">
              {/* Email Card */}
              <div className="glass-effect-card rounded-2xl p-6 transition-all duration-300 transform hover:scale-105 group">
                <div className="flex items-center justify-between">
                  <div className="bg-[#0d223f] p-3 rounded-full group-hover:scale-110 transition-transform">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 mr-3 ml-3">
                    <p className="text-gray-600 text-sm mb-1">
                      {t("Email us at")}
                    </p>
                    <a
                      href="mailto:info@maagoud.qa"
                      className="text-lg font-semibold text-gray-800 hover:text-[#0d223f] transition-colors"
                    >
                      {EMAIL_INFO}
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone Card */}
              <div className="glass-effect-card rounded-2xl p-6 transition-all duration-300 transform hover:scale-105 group">
                <div className="flex items-center justify-between">
                  <div className="bg-[#0d223f] p-3 rounded-full group-hover:scale-110 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 mr-3 ml-3">
                    <p className="text-gray-600 text-sm mb-1">
                      {t("Contact us")}
                    </p>
                    <a
                      href="tel:+97471077107"
                      dir="ltr"
                      className="text-lg font-semibold text-gray-800 hover:text-[#0d223f] transition-colors"
                    >
                      {CONTACT_INFO}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Section */}
            <div className="glass-effect-card rounded-2xl overflow-hidden transition-all duration-300">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="bg-[#0d223f] p-3 rounded-full">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 mr-3 ml-3">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {t("MAAGOUD TRADING")}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {t("Office -2, Building, 208 Street, Doha, Qatar")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Embed */}
              <div className="relative h-96 bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.6277834748477!2d51.52!3d25.28!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDE2JzQ4LjAiTiA1McKwMzEnMTIuMCJF!5e0!3m2!1sen!2sqa!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                ></iframe>
                <a
                  href="https://maps.google.com/?q=Maagoud+Trading+Doha+Qatar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-4 left-16 bg-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold text-[#0d223f] hover:bg-[#0d223f] hover:text-white transition-all duration-300 flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  {t("View larger map")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
